import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import LoadingSpinner from 'components/common/loading-spinner';
import Modal from 'components/common/modal';
import { ConfigurationType, SystemOptionType } from 'data/types';
import { loadMoreSystemCombinations } from 'features/systems';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import useCustomSystemRefreshState from 'hooks/use-custom-system-refresh-state';
import { useDuctedCompareMap } from 'hooks/use-ducted-compare-map';
import useHybridLeftoverSpaceIds from 'hooks/use-hybrid-leftover-space-ids';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import useIsDuctedPathway from 'hooks/use-is-ducted-pathway';
import usePtacSystemsFetch from 'hooks/use-ptac-systems-fetch';
import { useStepTipSequence } from 'hooks/use-step-tip-sequence';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { getChooseSystemsTipSections } from 'utils/choose-systems-tips';
import { isCustomSystemRefreshPending } from 'utils/custom-system-refresh';
import {
  deadEndCandidateConfigIds,
  deadEndForConfigs
} from 'utils/dead-end-for-configs';
import { selectCompleteConfigurations } from 'utils/pathway-configurations';
import { comparedIdForSelection } from 'utils/ducted-compare-state';
import { DuctedCompareSelectRequest } from 'utils/ducted-rail-selections';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import { FILTER_MISMATCH_TAG } from 'utils/filter-mismatch';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';
import { combinationToSystemOption } from 'utils/valid-systems';
import ComparisonModal from './comparison-modal';
import ComparisonTabs from './comparison-tabs';
import DuctedBundleBar from './ducted-bundle-bar';
import DuctedSystemDetailPanel, {
  DuctedPanelSelection
} from './ducted-system-detail-panel';
import DuctedSystemsCount from './ducted-systems-count';
import DuctedSystemsRail from './ducted-systems-rail';
import DuctlessSystemsGrid from './ductless-systems-grid';
import { systemMatchesFilters } from './equipment-comparison-filters';
import EquipmentComparisonMiddlePanel from './equipment-comparison-middle-panel';
import {
  PickKind,
  pickEligibleSystems,
  pickKindForSystem,
  preferCandidate,
  rankTopPicks,
  sortByOverallScore
} from './equipment-comparison-utils';
import { SwapListModel } from './swap-list-model';
import SystemFiltersPanel from './system-filters-panel';
import SystemSortSelect from './system-sort-select';
import SystemsCount from './systems-count';
import TonnageFilterBanner from './tonnage-filter-banner';
import useDuctedCompatibilityFetch from './use-ducted-compatibility-fetch';
import useEquipmentFilters from './use-equipment-filters';
import {
  getSystemProducts,
  useRebateEligibleProductIds
} from './use-rebate-availability';
import { SystemListViewMode } from './view-mode';

export default function EquipmentComparisonView() {
  const dispatch = useAppDispatch();
  const leftoverSpaceIds = useHybridLeftoverSpaceIds();
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const systemCombinationsFull = useAppSelector(
    (state) => state.systems.systemCombinationsFull
  );
  const combinationPaginationByConfig = useAppSelector(
    (state) => state.systems.combinationPaginationByConfig
  );
  const scenarioWinnersByConfig = useAppSelector(
    (state) => state.systems.scenarioWinnersByConfig
  );
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const customSystemRefreshState = useCustomSystemRefreshState();
  const matchingAirHandlersMode = useAppSelector(
    (state) => state.ui.matchingAirHandlersMode
  );
  const preferMatching = matchingAirHandlersMode !== 'off';
  const { addSystem, removeSystem } = useComparisonSelection();
  const [isComparisonModalOpen, setComparisonModalOpen] = useState(false);

  // Ducted compatible-combinations fetches (AHA-6681 CS-1) — page-level so
  // CS-2's middle column can read the same slice; internally gated to the
  // ducted pathway (no fetches on ductless).
  useDuctedCompatibilityFetch();
  // PTAC systems fetch — page-level for the same reason; internally gated to
  // the PTAC pathway with a complete design.
  usePtacSystemsFetch();

  const isDucted = useIsCompatibilityPathway();
  // The tonnage filter banner sizes on the tonnage scale — ducted only; the
  // PTAC pathway runs the same pipeline but sizes by BTU class.
  const isDuctedTonnagePathway = useIsDuctedPathway();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  // Same eligibility-based gate as ChooseSystemsPage (AHA-6681 CS-2): with
  // eligible ducted systems the view must render even when the ductless
  // pipeline produced zero options.
  const hasEligibleDuctedSystems = useMemo(
    () =>
      isDucted &&
      getChooseSystemsEligibleDuctedSystems(
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem
      ).length > 0,
    [
      isDucted,
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );

  // Ducted rail card selection (AHA-6681 CS-3) — lifted here so the middle
  // column can render the ducted detail panel for it. Cleared if the
  // pathway flips ductless, guarding a stale snapshot across a switch.
  const [ductedSelection, setDuctedSelection] =
    useState<DuctedPanelSelection | null>(null);
  const [ductedDetailsOpen, setDuctedDetailsOpen] = useState(false);
  const [ductlessDetailsOpen, setDuctlessDetailsOpen] = useState(false);
  const { comparedIdByCardId, selectionBySystemId, mapComparedId } =
    useDuctedCompareMap();
  const ductedComparedId = comparedIdForSelection(
    comparedIdByCardId,
    systemsToCompare,
    ductedSelection
  );
  const [ductedSelectRequest, setDuctedSelectRequest] =
    useState<DuctedCompareSelectRequest | null>(null);
  useEffect(() => {
    if (isDucted) {
      setDuctlessDetailsOpen(false);
    } else {
      setDuctedSelection(null);
      setDuctedDetailsOpen(false);
    }
  }, [isDucted]);

  const handleDuctedSelect = (selection: DuctedPanelSelection | null) => {
    setDuctedSelection(selection);
    if (selection === null) setDuctedDetailsOpen(false);
  };

  const handleInspectDucted = (selection: DuctedPanelSelection) => {
    setDuctedSelection(selection);
    setDuctedDetailsOpen(true);
  };

  const { allSystemOptions, filterState } = useEquipmentFilters();
  const hasSystemOptions = allSystemOptions.length > 0;

  // Why the build produced nothing for the configurations this list shows
  // (spec 001 T004): set only when every candidate configuration dead-ended,
  // so an empty list that the client-side filters caused keeps the generic
  // note. Candidates come from the complete configurations, not from
  // filterState.configIds alone: a configuration whose request degraded has
  // no combinations entry and so never enters the filter, yet its
  // scoring_unavailable diagnosis is exactly what the list should say.
  const deadEndByConfig = useAppSelector(
    (state) => state.systems.deadEndByConfig
  );
  const completeConfigIds = useAppSelector(
    (state) =>
      selectCompleteConfigurations(state, leftoverSpaceIds).map(
        (config) => config.uniqueId
      ),
    shallowEqual
  );
  const deadEnd = useMemo(
    () =>
      deadEndForConfigs(
        deadEndByConfig,
        systemCombinationsFull,
        deadEndCandidateConfigIds({
          completeConfigIds,
          fetchedConfigs: systemCombinationsFull,
          selectedConfigIds: filterState.configIds
        })
      ),
    [
      deadEndByConfig,
      completeConfigIds,
      systemCombinationsFull,
      filterState.configIds
    ]
  );

  const textKeys = useAppSelector((state) => state.data.textKeys);
  const tipSections = useMemo(
    () => getChooseSystemsTipSections({ textKeys, ready: hasSystemOptions }),
    [textKeys, hasSystemOptions]
  );
  const { currentStepTipSequence, reopenTipSequence } = useStepTipSequence({
    sections: tipSections,
    currentSectionId: 'choose_systems',
    currentStepComplete: false,
    suppressed: isComparisonModalOpen
  });

  const comparedIds = useMemo(
    () => new Set(systemsToCompare.map((s) => s.id)),
    [systemsToCompare]
  );

  const eligibleProductIds = useRebateEligibleProductIds(allSystemOptions);
  const rebateSystemIds = useMemo(
    () =>
      new Set(
        allSystemOptions
          .filter((system) =>
            getSystemProducts(system).some((p) =>
              eligibleProductIds.has(p.productId)
            )
          )
          .map((system) => system.id)
      ),
    [allSystemOptions, eligibleProductIds]
  );

  const rankedSystems = useMemo(
    () =>
      allSystemOptions.filter(
        (option) =>
          !option.tags.includes('Custom') &&
          systemMatchesFilters(option, filterState)
      ),
    [allSystemOptions, filterState]
  );

  const winnerOptionsByCode = useMemo<Map<string, SystemOptionType>>(() => {
    const configById = new Map<string, ConfigurationType>(
      configurations.map((c) => [c.uniqueId, c])
    );
    const result = new Map<string, SystemOptionType>();
    Object.entries(scenarioWinnersByConfig).forEach(([configId, winners]) => {
      if (!filterState.configIds.has(configId)) return;
      const config = configById.get(configId);
      if (!config) return;
      winners.forEach((combination) => {
        if (combination.tags.includes('Custom')) return;
        const option = combinationToSystemOption(combination, config);
        if (!option) return;
        // The API leaves selectedByScenarios empty for mismatched combos;
        // guard locally so a mismatched option can never seed a pick slot.
        if (option.tags.includes(FILTER_MISMATCH_TAG)) return;
        if (!systemMatchesFilters(option, filterState)) return;
        combination.selectedByScenarios.forEach((code) => {
          const incumbent = result.get(code) ?? null;
          if (
            preferCandidate(
              option,
              incumbent,
              (o) => o.score?.totalsByScenario?.[code] ?? 0,
              { preferMatching }
            )
          ) {
            result.set(code, option);
          }
        });
      });
    });
    return result;
  }, [scenarioWinnersByConfig, configurations, filterState, preferMatching]);

  const sortedByOverall = useMemo(
    () => sortByOverallScore(rankedSystems, { preferMatching }),
    [rankedSystems, preferMatching]
  );

  const pickEligible = useMemo(
    () => pickEligibleSystems(rankedSystems),
    [rankedSystems]
  );

  const picks = useMemo(
    () => rankTopPicks(pickEligible, winnerOptionsByCode, { preferMatching }),
    [pickEligible, winnerOptionsByCode, preferMatching]
  );

  const [showMoreRequested, setShowMoreRequested] = useState(false);
  // With no scored picks (PTAC systems carry no match score) there is
  // nothing to reveal behind the fold, so every system shows at once.
  const showMore = showMoreRequested || picks.list.length === 0;
  const [viewMode, setViewMode] = useState<SystemListViewMode>('compact');

  const pickIds = useMemo(
    () => new Set(picks.list.map((p) => p.id)),
    [picks.list]
  );
  // Custom systems come from the unfiltered pool: the user's own creations
  // always stay visible and swappable regardless of active filters.
  const customSystems = useMemo(
    () =>
      sortByOverallScore(
        allSystemOptions.filter((s) => s.tags.includes('Custom')),
        { preferMatching }
      ),
    [allSystemOptions, preferMatching]
  );
  const moreSystems = useMemo(
    () => sortedByOverall.filter((s) => !pickIds.has(s.id)),
    [sortedByOverall, pickIds]
  );

  const fallbackOrder = useMemo(
    () => [...customSystems, ...picks.list, ...moreSystems],
    [customSystems, picks.list, moreSystems]
  );

  const systemNumberById = useMemo(() => {
    const map = new Map<string, number>();
    fallbackOrder.forEach((system, index) => {
      if (!map.has(system.id)) map.set(system.id, index + 1);
    });
    return map;
  }, [fallbackOrder]);

  const displayedSystems = useMemo(() => {
    if (!showMore) return [...customSystems, ...picks.list];
    return [...customSystems, ...picks.list, ...moreSystems];
  }, [showMore, customSystems, picks.list, moreSystems]);

  const [selectedId, setSelectedId] = useState<string | null>(
    picks.bestOverall?.id ?? null
  );
  const [pendingSelectedId, setPendingSelectedId] = useState<string | null>(
    null
  );
  const [cardScrollRequest, setCardScrollRequest] = useState<{
    id: string;
    nonce: number;
  } | null>(null);

  useEffect(() => {
    if (pendingSelectedId === null) return;
    if (displayedSystems.some((s) => s.id === pendingSelectedId)) {
      setSelectedId(pendingSelectedId);
      setPendingSelectedId(null);
      return;
    }
    if (rankedSystems.some((s) => s.id === pendingSelectedId)) {
      setShowMoreRequested(true);
      return;
    }
    if (comparedIds.has(pendingSelectedId)) {
      setSelectedId(pendingSelectedId);
      setPendingSelectedId(null);
      return;
    }
    if (allSystemOptions.some((s) => s.id === pendingSelectedId)) {
      setPendingSelectedId(null);
    }
  }, [
    pendingSelectedId,
    displayedSystems,
    rankedSystems,
    comparedIds,
    allSystemOptions
  ]);

  useEffect(() => {
    if (selectedId === null) {
      if (picks.bestOverall) setSelectedId(picks.bestOverall.id);
      return;
    }
    if (comparedIds.has(selectedId)) return;
    if (customSystems.some((s) => s.id === selectedId)) return;
    if (!rankedSystems.some((s) => s.id === selectedId)) {
      setSelectedId(picks.bestOverall?.id ?? null);
    }
  }, [
    selectedId,
    picks.bestOverall,
    rankedSystems,
    customSystems,
    comparedIds
  ]);

  const comparedSelectedSystem = useMemo(() => {
    if (selectedId === null) return null;
    if (displayedSystems.some((s) => s.id === selectedId)) return null;
    const compared = systemsToCompare.find((s) => s.id === selectedId);
    if (!compared) return null;
    return allSystemOptions.find((s) => s.id === selectedId) ?? compared;
  }, [selectedId, displayedSystems, systemsToCompare, allSystemOptions]);

  const selectedIndex = useMemo(() => {
    if (comparedSelectedSystem) {
      return systemsToCompare.findIndex(
        (s) => s.id === comparedSelectedSystem.id
      );
    }
    if (displayedSystems.length === 0) return -1;
    const idx = displayedSystems.findIndex((o) => o.id === selectedId);
    return idx >= 0 ? idx : 0;
  }, [comparedSelectedSystem, systemsToCompare, displayedSystems, selectedId]);

  const selectedSystem: SystemOptionType | null =
    comparedSelectedSystem ??
    (selectedIndex >= 0 ? displayedSystems[selectedIndex] : null);

  const selectedPickKind = useMemo(
    () => pickKindForSystem(picks, selectedSystem),
    [picks, selectedSystem]
  );

  const selectedConfigId = selectedSystem?.configuration.uniqueId ?? null;
  const isSwapRefreshPending =
    selectedConfigId !== null &&
    isCustomSystemRefreshPending(customSystemRefreshState, selectedConfigId);

  const handleSelectSystem = useCallback((system: SystemOptionType) => {
    setPendingSelectedId(null);
    setSelectedId(system.id);
    setDuctlessDetailsOpen(true);
  }, []);

  const handleSelectComparedSystem = (system: SystemOptionType) => {
    const ducted = selectionBySystemId.get(system.id);
    if (ducted) {
      handleInspectDucted(ducted);
    } else if (isDucted) {
      setDuctedSelectRequest((previous) => ({
        systemId: system.id,
        nonce: (previous?.nonce ?? 0) + 1
      }));
    } else {
      setDuctlessDetailsOpen(true);
    }
    setPendingSelectedId(null);
    setSelectedId(system.id);
    if (
      !displayedSystems.some((s) => s.id === system.id) &&
      rankedSystems.some((s) => s.id === system.id)
    ) {
      setShowMoreRequested(true);
    }
    setCardScrollRequest((previous) => ({
      id: system.id,
      nonce: (previous?.nonce ?? 0) + 1
    }));
  };

  const handleUnselectSystem = useCallback(
    (system: SystemOptionType) => {
      removeSystem(system.id);
    },
    [removeSystem]
  );

  const handleAddSystemToCompare = useCallback(
    (system: SystemOptionType, kind: PickKind | null) => {
      addSystem(system, kind);
    },
    [addSystem]
  );

  const enabledConfigIds = useMemo(
    () => Array.from(filterState.configIds),
    [filterState.configIds]
  );

  const configIdsWithMore = useMemo(
    () =>
      enabledConfigIds.filter((configId) => {
        const pagination = combinationPaginationByConfig[configId];
        if (!pagination) return false;
        const loaded = systemCombinationsFull[configId]?.length ?? 0;
        return loaded < pagination.total;
      }),
    [enabledConfigIds, combinationPaginationByConfig, systemCombinationsFull]
  );

  const serverHasMore = configIdsWithMore.length > 0;

  const isLoadingMore = useMemo(
    () =>
      enabledConfigIds.some(
        (configId) => combinationPaginationByConfig[configId]?.loadingMore
      ),
    [enabledConfigIds, combinationPaginationByConfig]
  );

  const handleLoadMore = useCallback(() => {
    configIdsWithMore.forEach((configId) => {
      dispatch(loadMoreSystemCombinations(configId));
    });
  }, [dispatch, configIdsWithMore]);

  const ptacOptionCount = useMemo(
    () => allSystemOptions.filter(isPtacSelectedSystem).length,
    [allSystemOptions]
  );

  const galleryTotalCount = useMemo(
    () =>
      enabledConfigIds.reduce((sum, configId) => {
        const pagination = combinationPaginationByConfig[configId];
        return (
          sum +
          (pagination?.total ?? systemCombinationsFull[configId]?.length ?? 0)
        );
      }, ptacOptionCount),
    [
      enabledConfigIds,
      combinationPaginationByConfig,
      systemCombinationsFull,
      ptacOptionCount
    ]
  );

  const handleToggleShowMore = useCallback(
    () => setShowMoreRequested((v) => !v),
    []
  );

  const swapListModel = useMemo<SwapListModel>(
    () => ({
      picks,
      customSystems,
      moreSystems,
      rebateSystemIds,
      showMore,
      onToggleShowMore: handleToggleShowMore,
      viewMode,
      onViewModeChange: setViewMode,
      serverHasMore,
      isLoadingMore,
      onLoadMore: handleLoadMore,
      shownCount: customSystems.length + sortedByOverall.length,
      totalCount: galleryTotalCount
    }),
    [
      picks,
      customSystems,
      moreSystems,
      rebateSystemIds,
      showMore,
      handleToggleShowMore,
      viewMode,
      serverHasMore,
      isLoadingMore,
      handleLoadMore,
      sortedByOverall.length,
      galleryTotalCount
    ]
  );

  const hasCompleteConfigurations = useAppSelector(
    (state) => selectCompleteConfigurations(state, leftoverSpaceIds).length > 0
  );

  if (
    allSystemOptions.length === 0 &&
    !hasEligibleDuctedSystems &&
    !hasCompleteConfigurations
  ) {
    return (
      <div className='flex min-h-[600px] w-full flex-1 items-center justify-center text-gray-400'>
        No systems available.
      </div>
    );
  }

  const noMatchingSystems =
    !isDucted && rankedSystems.length === 0 && customSystems.length === 0;

  return (
    <div className='relative flex min-h-[600px] w-full flex-1 gap-4'>
      <div className='flex w-[260px] shrink-0 flex-col gap-3 overflow-y-auto'>
        <SystemFiltersPanel />
        {isDuctedTonnagePathway && <TonnageFilterBanner />}
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-3'>
        <div id='comparison-tabs' className='flex shrink-0 items-center gap-3'>
          <div className='min-w-0 grow'>
            <ComparisonTabs
              onSelectSystem={handleSelectComparedSystem}
              onOpenComparisonModal={() => setComparisonModalOpen(true)}
            />
          </div>
          <SystemSortSelect />
          {currentStepTipSequence && (
            <button
              type='button'
              className='flex shrink-0 items-center text-gray-400 hover:text-blue-500'
              onClick={reopenTipSequence}
              aria-label='Show page tips'
            >
              <QuestionMarkCircleIcon className='h-6 w-6' />
            </button>
          )}
        </div>
        <div className='relative min-h-0 flex-1'>
          <div className='flex h-full min-w-0 flex-col overflow-y-auto overflow-x-hidden rounded-lg'>
            {isDucted ? (
              <div className='flex min-w-0 flex-col gap-3'>
                <DuctedBundleBar
                  onInspectSlot={(slot) => {
                    if (slot.pick) handleInspectDucted(slot.pick.selection);
                  }}
                />
                <DuctedSystemsRail
                  selectedCardId={ductedSelection?.cardId ?? null}
                  onSelectCard={handleDuctedSelect}
                  onInspectCard={handleInspectDucted}
                  comparedIdByCardId={comparedIdByCardId}
                  onComparedIdMapped={mapComparedId}
                  selectComparedRequest={ductedSelectRequest}
                />
              </div>
            ) : (
              <DuctlessSystemsGrid
                picks={picks}
                customSystems={customSystems}
                moreSystems={moreSystems}
                systemNumberById={systemNumberById}
                selectedId={selectedSystem?.id ?? null}
                onSelect={handleSelectSystem}
                onUnselect={handleUnselectSystem}
                onAddToCompare={handleAddSystemToCompare}
                comparedIds={comparedIds}
                rebateSystemIds={rebateSystemIds}
                showMore={showMore}
                onToggleShowMore={handleToggleShowMore}
                serverHasMore={serverHasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
                scrollRequest={cardScrollRequest}
                noMatchingSystems={noMatchingSystems}
                deadEnd={deadEnd}
              />
            )}
          </div>
          {isSwapRefreshPending && (
            <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/60'>
              <LoadingSpinner />
              <span className='text-sm font-medium text-gray-600'>
                Updating systems...
              </span>
            </div>
          )}
        </div>
        <div className='shrink-0'>
          {isDucted ? (
            <DuctedSystemsCount />
          ) : (
            <SystemsCount
              shownCount={customSystems.length + sortedByOverall.length}
              totalCount={galleryTotalCount}
            />
          )}
        </div>
      </div>
      {isDucted ? (
        <Modal
          openState={[ductedDetailsOpen, setDuctedDetailsOpen]}
          title='System Details'
          size='xl'
          infoMode
          scrollableBody
          onClose={() => setDuctedSelection(null)}
        >
          <DuctedSystemDetailPanel
            selection={ductedSelection}
            comparedSystemId={ductedComparedId}
            onComparedIdMapped={mapComparedId}
            onComponentSwap={handleInspectDucted}
          />
        </Modal>
      ) : (
        <Modal
          openState={[ductlessDetailsOpen, setDuctlessDetailsOpen]}
          title='System Details'
          size='xl'
          infoMode
          scrollableBody
        >
          <EquipmentComparisonMiddlePanel
            system={selectedSystem}
            pickKind={selectedPickKind}
            currentIndex={selectedIndex}
            onCustomSystemSaved={setPendingSelectedId}
          />
        </Modal>
      )}
      <ComparisonModal
        openState={[isComparisonModalOpen, setComparisonModalOpen]}
        picks={picks}
        swapListModel={swapListModel}
        systemNumberById={systemNumberById}
        comparedIdByCardId={comparedIdByCardId}
        onComparedIdMapped={mapComparedId}
      />
    </div>
  );
}
